import * as React from 'react'
import { create } from '@bufbuild/protobuf'

import {
  CalculateTransportationMileageRequestSchema,
  TransportationMileageDestinationSchema,
} from '@/gen/cortado/v1/cortado_pb'
import type { Item, TaxRate } from '@/gen/cortado/v1/invoices_pb'
import { getErrorDescription } from '@/lib/error-utils'
import { applyTransportationMileageToLineItems } from '@/lib/transportation-line-items'
import { useCalculateTransportationMileage } from '@/stores/transportation.store'
import type { TransportationMileageEvent } from '@/components/shared/transportation-mileage-dialog'
import type { QuoteLineItem } from './quote-line-items-table'

type AutoCalculationState =
  | { status: 'calculating' }
  | { status: 'within-threshold'; totalMiles: number; thresholdMiles: number }
  | { status: 'error'; message: string }

type KeyedAutoCalculationState = AutoCalculationState & { key: string }

type UseAutoTransportationMileageOptions = {
  documentKey: string
  organizationId: string
  events: TransportationMileageEvent[]
  transportationItem?: Item
  items: QuoteLineItem[]
  taxRates: TaxRate[]
  autoApplyTaxRateId?: string
  enabledByDefault: boolean
  hasTransportationLine: boolean
  disabled: boolean
  onItemsChange: (items: QuoteLineItem[]) => void
}

function useLatest<T>(value: T) {
  const ref = React.useRef(value)
  React.useEffect(() => {
    ref.current = value
  }, [value])
  return ref
}

export function useAutoTransportationMileage({
  documentKey,
  organizationId,
  events,
  transportationItem,
  items,
  taxRates,
  autoApplyTaxRateId,
  enabledByDefault,
  hasTransportationLine,
  disabled,
  onItemsChange,
}: UseAutoTransportationMileageOptions): {
  autoCalculation?: AutoCalculationState
  destinationCount: number
} {
  const calculateMileage = useCalculateTransportationMileage()
  const [keyedAutoCalculation, setKeyedAutoCalculation] = React.useState<KeyedAutoCalculationState>()
  const attemptedKeyRef = React.useRef<string | undefined>(undefined)
  const activeKeyRef = React.useRef<string | undefined>(undefined)
  const mountedRef = React.useRef(true)
  const destinations = events
    .filter((event) => !!event.location?.trim())
    .map((event) => ({ clientKey: event.key, location: event.location?.trim() || '' }))
  const itemsRef = useLatest(items)
  const onItemsChangeRef = useLatest(onItemsChange)
  const transportationItemRef = useLatest(transportationItem)
  const destinationsRef = useLatest(destinations)
  const taxRatesRef = useLatest(taxRates)
  const autoApplyTaxRateIdRef = useLatest(autoApplyTaxRateId)
  const calculateMileageRef = useLatest(calculateMileage)

  React.useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const shouldCalculate = Boolean(
    enabledByDefault
    && transportationItem
    && !hasTransportationLine
    && !disabled
    && destinations.length > 0,
  )
  const calculationKey = shouldCalculate && transportationItem
    ? JSON.stringify([
        documentKey,
        organizationId,
        transportationItem.id,
        transportationItem.title,
        transportationItem.description,
        transportationItem.price,
        transportationItem.mileageThresholdMiles,
        destinations,
      ])
    : undefined
  activeKeyRef.current = calculationKey

  React.useEffect(() => {
    if (!calculationKey || attemptedKeyRef.current === calculationKey) return

    attemptedKeyRef.current = calculationKey
    setKeyedAutoCalculation({ key: calculationKey, status: 'calculating' })

    const item = transportationItemRef.current
    const currentDestinations = destinationsRef.current
    if (!item || currentDestinations.length === 0) return

    void calculateMileageRef.current.mutateAsync(create(CalculateTransportationMileageRequestSchema, {
      organizationId,
      transportationItemId: item.id,
      roundTrip: true,
      destinations: currentDestinations.map((destination) => create(
        TransportationMileageDestinationSchema,
        destination,
      )),
    })).then((response) => {
      if (!mountedRef.current || activeKeyRef.current !== calculationKey) return

      if (!response.complete) {
        setKeyedAutoCalculation({
          key: calculationKey,
          status: 'error',
          message: 'Mileage could not be calculated for every event. Review the event locations and try again.',
        })
        return
      }

      if (response.billableMiles <= 0) {
        setKeyedAutoCalculation({
          key: calculationKey,
          status: 'within-threshold',
          totalMiles: response.totalMiles,
          thresholdMiles: response.thresholdMiles,
        })
        return
      }

      const currentItem = transportationItemRef.current
      if (!currentItem) return

      onItemsChangeRef.current(applyTransportationMileageToLineItems({
        items: itemsRef.current,
        transportationItem: currentItem,
        billableMiles: response.billableMiles,
        taxRates: taxRatesRef.current,
        autoApplyTaxRateId: autoApplyTaxRateIdRef.current,
      }))
    }).catch((error) => {
      if (!mountedRef.current || activeKeyRef.current !== calculationKey) return
      setKeyedAutoCalculation({
        key: calculationKey,
        status: 'error',
        message: getErrorDescription(error),
      })
    })
  }, [calculationKey, calculateMileageRef, destinationsRef, transportationItemRef, organizationId, itemsRef, onItemsChangeRef, taxRatesRef, autoApplyTaxRateIdRef])

  return {
    autoCalculation: keyedAutoCalculation?.key === calculationKey
      ? keyedAutoCalculation
      : undefined,
    destinationCount: destinations.length,
  }
}
